import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { Block, BlockType, ErrorStepData, isStepData, StepData, ViaSurveyStrength } from '@shared/models/step.model';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { EntityStatus } from '@shared/models/menu.model';
import { LocalStorageService } from '@shared/services/localStorage.service';
import { ParamsCourseContentAPICall } from '@app/courseContent/courseContent.component';
import { switchMap } from 'rxjs/operators';
import { AnalyticsService, GaEvent } from '@shared/services/analytics.service';

interface AllStepData {
  [key: string]: ReplaySubject<StepData | ErrorStepData>;
}

export type ShortCodeValue = string | null | ViaSurveyStrength[];

export interface ProgramDataCode {
  code: string;
  value: ShortCodeValue;
}

export interface ShortCodes {
  [shortCodeKey: string]: ShortCodeValue;
}

export interface ProgramDataPayload {
  markStepAsCompleted: boolean;
  fullUrl: string;
  programData: ProgramDataCode[];
}

export interface ProgramDataResponse {
  congrats: '<p>Well done, your progress has been saved. Continue to your next step below.</p>';
}

export interface Customisations {
  handleScrollWithinBlock?: boolean; // Used by lifemap block
  hideBreadcrumbs?: boolean;
  hideCompanyBanner?: boolean;
  hideFooter?: boolean;
  hideSnackbar?: boolean;
  hideStepTitle?: boolean;
}

export interface Hooks {
  buttonLeftActionOverride: (() => any) | null;
  buttonRightSaveStepOverride: (() => any) | null;
}

export enum ApiUrlProgramData {
  loggedIn = '/program_data',
  surveyNotLoggedIn = '/program_data_survey',
}

export enum ApiUrlStep {
  step = '/step',
  survey = '/survey',
}

@Injectable({
  providedIn: 'root',
})
export class CourseContentService {
  private _stepData: AllStepData = {};
  private _customisations: WritableSignal<Customisations> = signal<Customisations>({});
  private _hooks: WritableSignal<Hooks> = signal<Hooks>({
    buttonLeftActionOverride: null,
    buttonRightSaveStepOverride: null,
  });
  private _isMainFormSubmitted: WritableSignal<boolean> = signal<boolean>(false);
  private _isCurrentStepLocked: Signal<boolean> = computed(
    () => this._currentStepData()?.status === EntityStatus.locked,
  );
  private _isCurrentStepCompleted: Signal<boolean> = computed(
    () => this._currentStepData()?.status === EntityStatus.completed,
  );
  private _currentStepData: WritableSignal<StepData | null> = signal(null);

  private readonly KEY_LOCAL_STORAGE_SHORTCODES: string = 'shortcodes';

  private _shortCodesSyncedWithLocalStorage: WritableSignal<ShortCodes> = signal({});

  constructor(
    private analyticsService: AnalyticsService,
    private httpService: HttpService,
    private localStorageService: LocalStorageService,
    private participantService: ParticipantService,
  ) {
    effect(
      () => {
        this.participantService.participant();
        this._shortCodesSyncedWithLocalStorage.set(
          JSON.parse(this.localStorageService.getFromLocalStorage(this.KEY_LOCAL_STORAGE_SHORTCODES) || '{}'),
        );
        this._stepData = {};
        this.resetCurrentStepData();
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.isCurrentStepLocked()) {
          this.hideSnackbar();
        }
      },
      { allowSignalWrites: true },
    );
  }

  public resetCurrentStepData(): void {
    this._currentStepData.set(null);
    this.resetAllCustomisation();
    this._hooks.set({
      buttonLeftActionOverride: null,
      buttonRightSaveStepOverride: null,
    });
    this.setMainFormSubmitted(false);
  }

  public getStepData(
    apiUrl: ApiUrlStep,
    courseContentSlug: string,
    paramsApi: ParamsCourseContentAPICall,
  ): Observable<StepData | ErrorStepData> {
    if (!this._stepData[courseContentSlug]) {
      this._stepData[courseContentSlug] = new ReplaySubject<StepData | ErrorStepData>(1);
      this.retrieveStepDataFromServer(apiUrl, courseContentSlug, paramsApi);
      return this._stepData[courseContentSlug].asObservable();
    }

    return this._stepData[courseContentSlug].pipe(
      switchMap((currentStepData: StepData | ErrorStepData) => {
        if (isStepData(currentStepData)) {
          const isStepLocked: boolean = currentStepData.status === EntityStatus.locked;
          const doesStepContainsModuleOverviewBlock: boolean =
            this.doesStepContainsModuleOverviewBlock(currentStepData);
          if (isStepLocked || doesStepContainsModuleOverviewBlock) {
            // if the step is locked, we retrieve it again in case it got "unlocked" because the user completed the previous step
            // module_overview block will change content when other step within the same module are completed, the data inside
            // the block is not enough 9missing ids) to update it manually, so it's easier to retrieve the step data from the server
            this._stepData[courseContentSlug] = new ReplaySubject<StepData | ErrorStepData>(1);
            this.retrieveStepDataFromServer(apiUrl, courseContentSlug, paramsApi);
          }
        }

        return this._stepData[courseContentSlug].asObservable();
      }),
    );
  }

  private doesStepContainsModuleOverviewBlock(stepData: StepData): boolean {
    for (const block of stepData.blocks) {
      if (block && block.element === BlockType.moduleOverview) {
        return true;
      }
    }

    return false;
  }

  public updateCurrentStepDataManually(slug: string, stepDataToUpdate: StepData): void {
    console.log('TONIO CourseContentService updateCurrentStepDataManually this._stepData=', this._stepData);
    if (!this._stepData[slug]) {
      throw new Error(
        `When updating stepData manually (slug="${slug}"), it's not normal that the ReplaySubject is not created`,
      );
    }
    this._stepData[slug].next(stepDataToUpdate);
    this._currentStepData.set(stepDataToUpdate);
  }

  private retrieveStepDataFromServer(
    apiUrl: string,
    courseContentSlug: string,
    paramsApi: ParamsCourseContentAPICall,
  ): void {
    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.GET,
      url: apiUrl,
      params: paramsApi,
    };

    this.httpService.request(requestOptions).subscribe({
      next: (stepData: StepData) => {
        if (!stepData.fullUrl) {
          // 360-Survey pages are not sending back fullUrl, this is a small hack to get around the shitty data coming back from the server
          stepData.fullUrl = courseContentSlug;
        }

        if (this._stepData[courseContentSlug]) {
          this._stepData[courseContentSlug].next(stepData);
        } else {
          // Edge case: if when retrieving Menu after completing a step, the back end replies with 401 to the menu endpoint
          // the user will be logged out => we will reset "this._stepData", in theory, we should never reach here
          // as if the menu endpoints replies with 401, the step endpoint should also reply with 401
          // Note: we can end up like this because I decided not to "wait" for the menu endpoint to finish before going to
          //       the next step upon step completion
          console.error(`_stepData is not initialised, it might be normal if token expired for menu endpoint`);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error(`CourseContentService: Error while retrieving step data: error=`, error);
        this._stepData[courseContentSlug].next({ error });
      },
    });
  }

  public get isMainFormSubmitted(): Signal<boolean> {
    return this._isMainFormSubmitted.asReadonly();
  }

  public setMainFormSubmitted(isFormSubmitted: boolean): void {
    this._isMainFormSubmitted.set(isFormSubmitted);
  }

  public get isCurrentStepLocked(): Signal<boolean> {
    return this._isCurrentStepLocked;
  }

  public get isCurrentStepCompleted(): Signal<boolean> {
    return this._isCurrentStepCompleted;
  }

  public get currentStepData(): Signal<StepData | null> {
    return this._currentStepData.asReadonly();
  }

  public setCurrentStepData(stepData: StepData): void {
    if (!this._stepData[stepData.slug]) {
      this._stepData[stepData.slug] = new ReplaySubject<StepData | ErrorStepData>(1);
    }

    this._currentStepData.set(stepData);
  }

  public saveCurrentStepAndRefreshMenu(
    apiUrl: ApiUrlProgramData,
    programDataCodesToSave: ProgramDataCode[],
    blocksUpdated: (Block | null)[],
    retrieveMenu: boolean,
  ): Promise<StepData> {
    console.log(
      'TONIO CourseContentService saveCurrentStepAndRefreshMenu programDataCodesToSave=',
      programDataCodesToSave,
      '   blocksUpdated=',
      blocksUpdated,
      '   retrieveMenu=',
      retrieveMenu,
    );
    const currentStepData: StepData | null = this.currentStepData();
    if (currentStepData) {
      const stepDataUpdated: StepData = {
        ...currentStepData,
        blocks: blocksUpdated,
        status: EntityStatus.completed,
      };

      const fullUrl: string = currentStepData.fullUrl;
      const payloadToSaveStepInServer: ProgramDataPayload = {
        markStepAsCompleted: true,
        fullUrl,
        programData: programDataCodesToSave,
      };

      return new Promise<StepData>((resolve): void => {
        const requestOptions: HttpQueryInterface = {
          type: HttpRequestType.POST,
          url: apiUrl,
          body: payloadToSaveStepInServer,
        };

        this.httpService.request(requestOptions).subscribe({
          next: (): void => {
            console.log('TONIO CourseContentService next SUCCESS =');
            this.clearShortCodeSavedInLocalStorage();

            if (retrieveMenu) {
              // We're not "awaiting" this one as we don't need to update the menu before the user navigate to the next step.
              this.participantService.retrieveMenu();
            }

            this.analyticsService.sendEvent(GaEvent.stepCompleted);

            this.updateCurrentStepDataManually(stepDataUpdated.fullUrl, stepDataUpdated);
            resolve(stepDataUpdated);
          },
          error: (error: HttpErrorResponse): void => {
            console.error(`Error while saving current step: error=`, error);
          },
        });
      });
    } else {
      throw new Error(`No StepData when saving a step, this is not normal`);
    }
  }

  public saveProgramDataCodesAndUpdateStep(
    apiUrl: ApiUrlProgramData,
    programDataCodesToSave: ProgramDataCode[],
    blockToUpdateInCurrentStep: Block,
  ): Promise<void> {
    return new Promise<void>((resolve): void => {
      const currentStepData: StepData | null = this.currentStepData();
      if (currentStepData) {
        const fullUrl: string = currentStepData.fullUrl;
        const payloadToSaveStepInServer: ProgramDataPayload = {
          markStepAsCompleted: false,
          fullUrl,
          programData: programDataCodesToSave,
        };

        const requestOptions: HttpQueryInterface = {
          type: HttpRequestType.POST,
          url: apiUrl,
          body: payloadToSaveStepInServer,
        };

        this.httpService.request(requestOptions).subscribe({
          next: (): void => {
            const stepDataUpdated: StepData = {
              ...currentStepData,
              blocks: currentStepData.blocks.map((oldBlock: Block | null) => {
                if (oldBlock && oldBlock.id === blockToUpdateInCurrentStep.id) {
                  return blockToUpdateInCurrentStep;
                } else {
                  return oldBlock;
                }
              }),
            };

            this.updateCurrentStepDataManually(stepDataUpdated.fullUrl, stepDataUpdated);
            resolve();
          },
          error: (error: HttpErrorResponse): void => {
            console.error(`Error while saving program data codes: error=`, error);
          },
        });
      } else {
        throw new Error(`currentStepData is null, this should never happen`);
      }
    });
  }

  public getShortcodes(shortcodeList: string[]): Promise<ShortCodes> {
    return new Promise((resolve): void => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.GET,
        url: `/program_data`,
        params: {
          shortcodes: JSON.stringify(shortcodeList),
        },
      };

      this.httpService.request(requestOptions).subscribe({
        next: (response: ShortCodes): void => {
          resolve(response);
        },
        error: (error: HttpErrorResponse): void => {
          console.error(`Error while retrieving program data codes: error=`, error);
        },
      });
    });
  }

  public saveShortCodesInLocalStorage(shortCodesToSave: ShortCodes): void {
    const shortCodesInLocalStorage: ShortCodes = {
      ...this._shortCodesSyncedWithLocalStorage(),
      ...shortCodesToSave,
    };

    this._shortCodesSyncedWithLocalStorage.set(shortCodesInLocalStorage);
    const valueToStore: string = JSON.stringify(this._shortCodesSyncedWithLocalStorage());
    this.localStorageService.storeInLocalStorage(this.KEY_LOCAL_STORAGE_SHORTCODES, valueToStore);
  }

  public getShortCodeSavedInLocalStorage(shortCode: string): ShortCodeValue {
    const shortCodesInLocalStorage: ShortCodes = this._shortCodesSyncedWithLocalStorage();
    return shortCodesInLocalStorage[shortCode] || null;
  }

  private clearShortCodeSavedInLocalStorage(): void {
    this._shortCodesSyncedWithLocalStorage.set({});
    this.localStorageService.removeFromLocalStorage(this.KEY_LOCAL_STORAGE_SHORTCODES);
  }

  public get customisations(): Signal<Customisations> {
    return this._customisations.asReadonly();
  }

  public get hooks(): Signal<Hooks> {
    return this._hooks.asReadonly();
  }

  public resetAllCustomisation(): void {
    this._customisations.set({});
  }

  public addOverrideForButtonLeftAction(fn: (() => any) | null): void {
    this._hooks.update((hooks: Hooks) => {
      return {
        ...hooks,
        buttonLeftActionOverride: fn,
      };
    });
  }

  public addOverrideForButtonRightAction(fn: (() => any) | null): void {
    this._hooks.update((hooks: Hooks) => {
      return {
        ...hooks,
        buttonRightSaveStepOverride: fn,
      };
    });
  }

  public hideBreadcrumbs(): void {
    this.updateCustomisation('hideBreadcrumbs', true);
  }

  public hideStepTitle(): void {
    this.updateCustomisation('hideStepTitle', true);
  }

  public hideSnackbar(): void {
    this.updateCustomisation('hideSnackbar', true);
  }

  public hideCompanyBanner(): void {
    this.updateCustomisation('hideCompanyBanner', true);
  }

  public hideFooter(): void {
    this.updateCustomisation('hideFooter', true);
  }

  public handleScrollWithinBlock(): void {
    this.updateCustomisation('handleScrollWithinBlock', true);
  }

  public showSnackbar(): void {
    this.updateCustomisation('hideSnackbar', false);
  }

  private updateCustomisation(key: keyof Customisations, hide: boolean): void {
    this._customisations.update((customisations: Customisations): Customisations => {
      return {
        ...customisations,
        [key]: hide,
      };
    });
  }

  public enableCustomisationsForSpecialSteps(stepData: StepData, forceCustomisations: Customisations | null): void {
    for (const block of stepData.blocks) {
      if (block) {
        const blockType: BlockType = block.element;
        if (blockType === BlockType.lifemap) {
          this.handleScrollWithinBlock();
          this.hideBreadcrumbs();
          this.hideCompanyBanner();
          this.hideFooter();
          this.hideSnackbar();
          this.hideStepTitle();
        }
      }
    }

    if (forceCustomisations) {
      if (forceCustomisations.hideBreadcrumbs) {
        this.hideBreadcrumbs();
      }

      if (forceCustomisations.hideStepTitle) {
        this.hideStepTitle();
      }

      if (forceCustomisations.hideSnackbar) {
        this.hideSnackbar();
      }
    }
  }
}
